import { SetStateAction } from 'react';
import { RouteLegFormMode } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { AviaLegTypes, GroundLegTypes, RouteLegTypeEnum } from '@/api/apis/route';

export enum WarningTypeEnum {
  BannedDriver = 'BannedDriver',
  BannedTailNumber = 'BannedTailNumber',
  SupplementalProviderCombination = 'SupplementalProviderCombination',
  MaximumDriveTime = 'MaximumDriveTime',
  GroundNotFeasible = 'GroundNotFeasible',
  AircraftUnavailable = 'AircraftUnavailable',
  AircraftOnRest = 'AircraftOnRest',
  AircraftOnDutyRange = 'AircraftOnDutyRange',
}

export const WARNING_MESSAGES_MAP: Record<WarningTypeEnum, string> = {
  [WarningTypeEnum.BannedDriver]:
    'A driver with similar details is marked as banned. Consider other driver. Contact your administrator for details.',
  [WarningTypeEnum.BannedTailNumber]:
    'A tail with similar number is marked as banned. Consider other plane. Contact your administrator for details.',
  [WarningTypeEnum.SupplementalProviderCombination]:
    'Different Supplemental Lift Providers for air legs are not supported within one route. The Supplemental Lift Provider specified in the last air leg shall be applied to all Supplemental Lift Providers air legs.',
  [WarningTypeEnum.MaximumDriveTime]: 'Maximum allowed drive time is 3 hours.',
  [WarningTypeEnum.AircraftUnavailable]:
    'The selected aircraft is currently unavailable due to maintenance or technical issues.',
  [WarningTypeEnum.AircraftOnRest]: `The selected aircraft's crew is on rest during the selected flight time range. Please assign an available crew or adjust the schedule.`,
  [WarningTypeEnum.AircraftOnDutyRange]: `The selected aircraft's availability does not fully cover the flight duration. Adjust crew schedules or flight timing to avoid disruptions.`,
  [WarningTypeEnum.GroundNotFeasible]: 'Ground leg is not feasible.',
};

export const messagesAddFactory = (
  setState: (value: SetStateAction<Map<WarningTypeEnum, string>>) => void,
  type: WarningTypeEnum,
  message?: string,
) =>
  setState((prev) => {
    if (prev.has(type)) return prev;
    const newMessages = new Map(prev);
    newMessages.set(type, message || WARNING_MESSAGES_MAP[type]);
    return newMessages;
  });

export const messagesRemoveFactory = (
  setState: (value: SetStateAction<Map<WarningTypeEnum, string>>) => void,
  type: WarningTypeEnum | WarningTypeEnum[],
) => {
  setState((prev) => {
    const newMessages = new Map(prev);

    if (Array.isArray(type)) {
      const existingMessages = type.filter((v) => newMessages.has(v));

      if (existingMessages.length) {
        existingMessages.forEach((t) => newMessages.delete(t));
        return newMessages;
      }
    } else {
      const hasMessage = newMessages.has(type);

      if (hasMessage) {
        newMessages.delete(type);
        return newMessages;
      }
    }

    return prev;
  });
};

export const checkSupplementalProviderError = (
  mode: RouteLegFormMode,
  legs: (AviaLegTypes | GroundLegTypes)[],
  newSupplementalProviderId: string,
  defaultSupplementalProviderId: string,
) => {
  const isEdit = mode === RouteLegFormMode.Edit;
  const supplementalLegs = legs.filter((l) => l.legType === RouteLegTypeEnum.SupplementalLiftAviaLeg);
  const isMultipleLegs = supplementalLegs.length > 1;
  const isDifferentProviderSelected =
    defaultSupplementalProviderId && newSupplementalProviderId !== defaultSupplementalProviderId;
  const hasProviderCheck = !isEdit || isMultipleLegs;

  return hasProviderCheck && isDifferentProviderSelected;
};
